/**
 * Pure filter-composition helpers for `PRM-151`.
 *
 * A map layer must have exactly one effective filter owner rather than
 * letting a preset, the Layers legend, and a geographic scope filter each
 * overwrite `layerView.filter`/`definitionExpression` independently (spec
 * section 7.4.1). These helpers are pure string/array composition only — they do
 * not touch `@arcgis/core` layer instances, so they can be unit tested
 * without a MapView and reused by any consumer (`FeatureLayer.definitionExpression`,
 * `FeatureLayerView.filter.where`, etc.).
 */

import type { CategoricalSymbologyPreset, SymbologyCategory } from './symbologyPresets';
import { categoryWhereClause } from './symbologyPresets';

/**
 * Named filter contributors composed into one effective WHERE clause. Every
 * field is optional so a layer with no active preset/legend/scope filter
 * still composes correctly (falls back to `1=1`).
 */
export interface FilterContributors {
  /** Base authorization/server-side scope predicate that must always remain intact (e.g. DataScope). */
  base?: string | null;
  /** Active symbology preset's category-inclusion predicate. */
  preset?: string | null;
  /** Layers legend (ad-hoc unique-value renderer toggle) predicate. */
  legend?: string | null;
  /** Geographic/state-province scope predicate. */
  scope?: string | null;
}

/**
 * Combines a categorical preset and a set of excluded category values into
 * a single WHERE clause that includes only the non-excluded categories.
 *
 * If every category is excluded, returns the literal `1=0` so the layer
 * shows no features without removing the layer or touching any other
 * predicate (spec sections 7.3.5, 7.4.3, and 8 "all categories excluded"). If no
 * categories are excluded, returns `null` (no restriction needed — every
 * value is already included), keeping the composed clause as short as
 * possible.
 *
 * @param preset - The categorical preset whose categories are being toggled.
 * @param excludedValues - The set of category values currently excluded from the view.
 * @returns A standalone WHERE fragment, the literal `1=0`, or `null` if nothing is excluded.
 *
 * @example
 * presetFilterClause(acquisitionStatusPreset, new Set(['Completed', 'Discontinued']))
 * // "(PotentialStatus = 'Pipeline' OR PotentialStatus = 'Negotiation' OR PotentialStatus = 'Under Contract')"
 * presetFilterClause(acquisitionStatusPreset, new Set(acquisitionStatusPreset.categories.map(c => c.value)))
 * // "1=0"
 * presetFilterClause(acquisitionStatusPreset, new Set())
 * // null
 */
export function presetFilterClause<TValue extends string>(
  preset: CategoricalSymbologyPreset<TValue>,
  excludedValues: ReadonlySet<TValue>
): string | null {
  const included: SymbologyCategory<TValue>[] = preset.categories.filter(
    (category) => !excludedValues.has(category.value)
  );

  if (included.length === 0) return '1=0';
  if (included.length === preset.categories.length) return null;

  return `(${included.map((category) => categoryWhereClause(preset, category)).join(' OR ')})`;
}

/**
 * Composes named filter contributors into one effective WHERE clause,
 * `AND`-joining every non-empty contributor so authorization/scope
 * predicates can never be silently dropped by a preset or legend change.
 *
 * Each contributor is wrapped in parentheses before joining so contributors
 * containing their own top-level `OR` (like {@link presetFilterClause}'s
 * output) combine safely with `AND`. Falls back to `1=1` when every
 * contributor is empty/omitted, matching ArcGIS's convention for "no
 * filter".
 *
 * @param contributors - Named filter contributors (`base`, `preset`, `legend`, `scope`).
 * @returns The composed effective WHERE clause.
 *
 * @example
 * composeFilters({ base: "STATUS='Active'", preset: "PotentialStatus = 'Pipeline'" })
 * // "(STATUS='Active') AND (PotentialStatus = 'Pipeline')"
 * composeFilters({})
 * // "1=1"
 */
export function composeFilters(contributors: FilterContributors): string {
  const clauses = [contributors.base, contributors.preset, contributors.legend, contributors.scope].filter(
    (clause): clause is string => Boolean(clause && clause.trim().length > 0)
  );

  if (clauses.length === 0) return '1=1';

  return clauses.map((clause) => `(${clause})`).join(' AND ');
}
